import {
	PARAGRAPH_KEY,
	HEADING_KEY,
	ORDERED_LIST_KEY,
	UNORDERED_LIST_KEY,
	SELECTION_KEY
} from '@techchange/pm-specs';
import { TextSelection } from 'prosemirror-state';

import { rebuildList } from '../list/commands/rebuildList';
import getMarkedSelection from '../utils/getMarkedSelection';
import markSelection from '../utils/markSelection';

/**
 * Updates the current selection to the specified typographical node type.
 *
 * @param {Object} nodeKey  The key of the node to switch to.
 * @param {Object} attrs    The heading attributes.
 *
 * @returns {Function} Parametrized command to update the selection.
 *
 */
export const toggleHeading = (nodeKey, attrs) => {

	return (state, dispatch) => {

		const { schema, tr, selection } = state;
		const { from, to } = selection;

		const nodeType = schema.nodes[nodeKey];

		const paragraphNodeType = schema.nodes[PARAGRAPH_KEY];
		const headingNodeType = schema.nodes[HEADING_KEY];
		const unorderedListNodeType = schema.nodes[UNORDERED_LIST_KEY];
		const orderedListNodeType = schema.nodes[ORDERED_LIST_KEY];
		const selectionMarkType = schema.marks[SELECTION_KEY];

		// Add mark to selected inline nodes to remember original selection
		markSelection(state, tr);

		const selectedNodes = [];

		let updatedNodes = [];

		tr.doc.nodesBetween(from, to, (node, pos) => {

			if (node.type === unorderedListNodeType || node.type === orderedListNodeType) {

				updatedNodes = updatedNodes.concat(rebuildList(node.type, node, pos, state, {
					merge: false,
					unwrap: true,
				}));

			} else {

				updatedNodes.push(node);

			}

			selectedNodes.push({ node, pos });

			// Don't recurse to child nodes
			return false;

		});

		// Replace the selection with the new content
		const firstNode = selectedNodes[0];
		const lastNode = selectedNodes[selectedNodes.length - 1];
		const start = firstNode.pos;
		const end = lastNode.pos + lastNode.node.nodeSize;

		tr.replaceWith(start, end, updatedNodes);

		const [newFrom, newTo] = getMarkedSelection(state, tr);

		// Find all of the first-level paragraph and heading nodes to update
		const nodesToUpdate = [];

		tr.doc.nodesBetween(newFrom, newTo, (node, pos) => {

			if (node.type === paragraphNodeType || node.type === headingNodeType) {
				nodesToUpdate.push([node, pos]);
			}

			// Don't recurse to child nodes
			return false;

		});

		nodesToUpdate.forEach(([node, pos]) => {

			const updatedNode = nodeType.create(attrs, node.content);

			tr.replaceWith(pos, pos + node.nodeSize, updatedNode);

		});

		// Update the selection and remove the selection marks
		const isCursorSelection = from === to;

		const newSelection = TextSelection.create(
			tr.doc,
			newFrom || 0,
			isCursorSelection ? newFrom : (newTo || 0)
		);

		tr.setSelection(newSelection);
		tr.removeMark(0, tr.doc.content.size, selectionMarkType);

		// Remove placeholder for cursor selections
		if (isCursorSelection) {
			tr.delete(newFrom, newTo);
		}

		if (dispatch) {
			dispatch(tr);
		}

		return true;

	};

};
